import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./LandingPage.module.css";
import {
  collection,
  addDoc,
  Timestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../Firebase/firebaseConfig";
import { Code, Database, Users } from "lucide-react";

// -------------------- STATIC DATA --------------------
const techFields = [
  {
    name: "CSE",
    icon: Code,
    label: "Computer Science",
    position: { top: "10%", left: "5%" },
    rotation: 15,
  },
  {
    name: "ECE",
    icon: Database,
    label: "Electronics & Comm.",
    position: { top: "45%", left: "8%" },
    rotation: 30,
  },
  {
    name: "MKT",
    icon: Users,
    label: "Marketing",
    position: { top: "85%", left: "80%" },
    rotation: 20,
  },
];

const courseDurations = {
  CSE: 4,
  ECE: 4,
  MKT: 3,
};

const quizPaths = {
  CSE: "/quiz/cse",
  ECE: "/quiz/ece",
  MKT: "/quiz/digital-marketing",
};

// -------------------- VALIDATION FUNCTIONS --------------------
const validateIndianMobile = (mobileNumber) =>
  /^[6-9]\d{9}$/.test(mobileNumber);
const validateRollNumber = (rollNumber) =>
  /^[a-zA-Z0-9\s_-]{2,20}$/.test(rollNumber);

// -------------------- COMPONENT --------------------
const LandingPage = () => {
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [yearOptions, setYearOptions] = useState([]);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedInterest, setSelectedInterest] = useState("");
  const [rollNumber, setRollNumber] = useState("");
  const [rollNumberError, setRollNumberError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true); // ✅ loader for 3–5 sec on page load
  const navigate = useNavigate();

  // Show loader for 1 second on page load
  useEffect(() => {
    const timer = setTimeout(() => setPageLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // -------------------- HANDLERS --------------------
  const handleDepartmentChange = (e) => {
    const dept = e.target.value;
    setSelectedDepartment(dept);
    const years = courseDurations[dept] || 0;
    setYearOptions(Array.from({ length: years }, (_, i) => i + 1));
    setSelectedYear("");
  };

  const handlePhoneChange = (e) => {
    const num = e.target.value;
    setPhone(num);
    setPhoneError(
      num && !validateIndianMobile(num)
        ? "Enter valid 10-digit mobile number (starts with 6–9)"
        : "",
    );
  };

  const handleRollNumberChange = (e) => {
    const roll = e.target.value;
    setRollNumber(roll);
    setRollNumberError(
      roll && !validateRollNumber(roll)
        ? "Invalid roll number (2–20 alphanumeric chars)"
        : "",
    );
  };

  const isFormValid = () =>
    userName &&
    userEmail &&
    selectedDepartment &&
    selectedYear &&
    selectedInterest &&
    phone &&
    rollNumber &&
    !phoneError &&
    !rollNumberError;

  // -------------------- SUBMIT --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    try {
      setLoading(true);

      const participantsRef = collection(db, "participants");
      const q = query(participantsRef, where("phone", "==", phone));
      const querySnapshot = await getDocs(q);

      const existingEntries = querySnapshot.docs.map((d) => d.data());
      const sameInterest = existingEntries.find(
        (e) => e.interest === selectedInterest,
      );

      if (sameInterest) {
        setLoading(false);
        alert("You already participated in this interest!");
        return;
      }

      const uniqueInterests = [
        ...new Set(existingEntries.map((e) => e.interest)),
      ];
      if (uniqueInterests.length >= 3) {
        setLoading(false);
        alert("You have reached the maximum participation limit (3).");
        return;
      }

      const userDetails = {
        name: userName,
        email: userEmail,
        department: selectedDepartment,
        year: selectedYear,
        interest: selectedInterest,
        phone,
        rollNumber,
        countryCode: "+91",
        submittedAt: Timestamp.now(),
      };

      await addDoc(collection(db, "participants"), userDetails);

      // simulate short loader before redirect
      setTimeout(() => {
        const quizPath = quizPaths[selectedDepartment] || "/";
        navigate(quizPath);
      }, 2000);
    } catch (err) {
      console.error("❌ Error saving data:", err);
      console.error("❌ Error saving data:", err);
      // alert("Something went wrong while saving your details!"); // Optional: keep or remove original alert
      alert(
        "Something went wrong while saving your details! Please contact support if this persists.",
      );
      setLoading(false);
    }
  };

  // -------------------- SHOW PAGE LOADER --------------------
  if (pageLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.loader}></div>
        <p className={styles.loaderText}>Please wait... </p>
      </div>
    );
  }

  // -------------------- SHOW SUBMIT LOADER --------------------
  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.loader}></div>
        <p className={styles.loaderText}>Please wait...</p>
      </div>
    );
  }

  // -------------------- MAIN UI --------------------
  return (
    <div className={styles.landingPage}>
      <h1 className={styles.pageTitle}>
        ZIION <span className={styles.highlight}>TECHNOLOGY</span>
      </h1>
      <h5>X</h5>
      <h2>PARIVARTAN</h2>

      <div className={styles.backgroundIcons}>
        {techFields.map((f) => (
          <div
            key={f.name}
            className={styles.bgIconWrapper}
            style={{
              top: f.position.top,
              left: f.position.left,
              transform: `rotate(${f.rotation}deg)`,
            }}
          >
            <f.icon size={80} className={styles.bgIcon} />
          </div>
        ))}
      </div>

      <div className={styles.contentRow}>
        <div className={styles.leftColumn}>
          <p className={styles.preHeading}>Hey Quizzers! 🧠</p>
          <h1 className={styles.mainHeading}>
            Ready to Challenge{" "}
            <span className={styles.highlight}>Your Mind and Win Big?</span>
          </h1>
          <p className={styles.description}>
            Join the ultimate quiz fest — compete, learn, and grab exciting
            prizes & gift vouchers!
          </p>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.formContainer}>
            <h2 className={styles.formHeading}>Ready to Win Prizes?</h2>
            <form onSubmit={handleSubmit}>
              <div className={styles.formRow}>
                <input
                  type="text"
                  placeholder="Enter Your Name"
                  className={styles.input}
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                />
                <input
                  type="email"
                  placeholder="Enter Your Email"
                  className={styles.input}
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  required
                />
              </div>

              <div className={styles.formRow}>
                <input
                  type="text"
                  placeholder="Roll Number"
                  className={styles.input}
                  value={rollNumber}
                  onChange={handleRollNumberChange}
                  required
                />
                <select
                  className={styles.select}
                  value={selectedDepartment}
                  onChange={handleDepartmentChange}
                  required
                >
                  <option value="">Select Department</option>
                  {techFields.map((f) => (
                    <option key={f.name} value={f.name}>
                      {f.label} ({f.name})
                    </option>
                  ))}
                </select>
              </div>
              {rollNumberError && (
                <div className={styles.error}>{rollNumberError}</div>
              )}

              <div className={styles.formRow}>
                <div className={styles.phoneInputContainer}>
                  <div className={styles.countryCodeDisplay}>+91</div>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    className={styles.phoneInput}
                    value={phone}
                    onChange={handlePhoneChange}
                    required
                  />
                </div>

                {yearOptions.length > 0 && (
                  <>
                    <select
                      className={styles.select}
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      required
                    >
                      <option value="">Select Year</option>
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                          {y === 1
                            ? "st"
                            : y === 2
                              ? "nd"
                              : y === 3
                                ? "rd"
                                : "th"}{" "}
                          Year
                        </option>
                      ))}
                    </select>

                    <select
                      className={styles.select}
                      value={selectedInterest}
                      onChange={(e) => setSelectedInterest(e.target.value)}
                      required
                    >
                      <option value="">Select Interest</option>
                      <option value="Coding">Coding</option>
                      <option value="Marketing">Marketing</option>
                      <option value="ECE">ECE</option>
                    </select>
                  </>
                )}
              </div>
              {phoneError && <div className={styles.error}>{phoneError}</div>}

              <button
                type="submit"
                className={styles.submitButton}
                disabled={!isFormValid() || loading}
              >
                {loading ? "Please wait..." : "PLAY NOW"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
